<template>
  <div class="faq-page">
    <!-- Hero Section -->
    <div class="faq-hero">
      <div class="hero-bg-shapes">
        <div class="shape s1"></div>
        <div class="shape s2"></div>
        <div class="shape s3"></div>
      </div>
      <div class="hero-content">
        <div class="hero-badge">
          <i class="el-icon-question"></i>
          <span>{{ $t('faq.hero.badge') }}</span>
        </div>
        <h1>{{ $t('faq.hero.title') }}</h1>
        <p>{{ $t('faq.hero.subtitle') }}</p>
        <div class="search-box">
          <i class="el-icon-search"></i>
          <input 
            v-model="searchQuery" 
            :placeholder="$t('faq.hero.searchPlaceholder')" 
            @input="handleSearch"
          />
          <span v-if="searchQuery" class="search-clear" @click="clearSearch">
            <i class="el-icon-circle-close"></i>
          </span>
        </div>
        <div class="quick-links">
          <span 
            v-for="(tag, idx) in quickTags" 
            :key="idx"
            class="quick-tag"
            @click="searchQuery = tag; handleSearch()"
          >{{ tag }}</span>
        </div>
      </div>
    </div>

    <!-- Category Filter -->
    <div class="faq-container">
      <div class="category-bar">
        <button 
          v-for="cat in categories" 
          :key="cat.id"
          class="category-btn"
          :class="{ active: activeCategory === cat.id }"
          @click="activeCategory = cat.id"
        >
          <i :class="cat.icon"></i>
          <span>{{ $t(`faq.categories.${cat.id}`) }}</span>
          <span class="cat-count">{{ getCategoryCount(cat.id) }}</span>
        </button>
      </div>

      <!-- Search results banner -->
      <div v-if="searchQuery" class="search-results-banner">
        <span>{{ $t('faq.searchResults', { count: filteredFaqs.length, query: searchQuery }) }}</span>
        <el-button type="text" @click="clearSearch">{{ $t('faq.clearSearch') }}</el-button>
      </div>

      <!-- FAQ Accordion -->
      <div class="faq-list">
        <transition-group name="faq-item" tag="div">
          <div 
            v-for="(item, index) in filteredFaqs" 
            :key="item.id"
            class="faq-card"
            :class="{ expanded: expandedId === item.id }"
          >
            <div class="faq-question" @click="toggleFaq(item.id)">
              <div class="q-left">
                <span class="q-number">{{ String(index + 1).padStart(2, '0') }}</span>
                <span class="q-category-dot" :class="item.category"></span>
                <h3>{{ $t(`faq.items.${item.id}.q`) }}</h3>
              </div>
              <div class="q-right">
                <el-tag size="mini" :type="getCategoryTagType(item.category)" effect="plain">
                  {{ $t(`faq.categories.${item.category}`) }}
                </el-tag>
                <div class="expand-icon" :class="{ rotated: expandedId === item.id }">
                  <i class="el-icon-arrow-down"></i>
                </div>
              </div>
            </div>
            <transition name="answer-slide">
              <div v-if="expandedId === item.id" class="faq-answer">
                <div class="answer-content">
                  <p v-html="$t(`faq.items.${item.id}.a`)"></p>
                </div>
                <div class="answer-footer">
                  <span class="helpful-text">{{ $t('faq.helpful') }}</span>
                  <div class="helpful-buttons">
                    <button 
                      class="helpful-btn yes" 
                      :class="{ voted: helpfulVotes[item.id] === 'yes' }"
                      @click="voteHelpful(item.id, 'yes')"
                    >
                      <i class="el-icon-check"></i> {{ $t('faq.yes') }}
                    </button>
                    <button 
                      class="helpful-btn no"
                      :class="{ voted: helpfulVotes[item.id] === 'no' }"
                      @click="voteHelpful(item.id, 'no')"
                    >
                      <i class="el-icon-close"></i> {{ $t('faq.no') }}
                    </button>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </transition-group>

        <!-- Empty state -->
        <div v-if="filteredFaqs.length === 0" class="empty-state">
          <i class="el-icon-search"></i>
          <h3>{{ $t('faq.noResults') }}</h3>
          <p>{{ $t('faq.noResultsDesc') }}</p>
          <el-button type="primary" @click="clearSearch" round>{{ $t('faq.showAll') }}</el-button>
        </div>
      </div>

      <!-- Contact CTA Section -->
      <div class="contact-cta">
        <div class="cta-content">
          <div class="cta-icon">
            <i class="el-icon-chat-dot-round"></i>
          </div>
          <h2>{{ $t('faq.cta.title') }}</h2>
          <p>{{ $t('faq.cta.subtitle') }}</p>
          <div class="cta-buttons">
            <el-button type="primary" round @click="$router.push('/login')">
              <i class="el-icon-service"></i>
              {{ $t('faq.cta.contactSupport') }}
            </el-button>
            <!-- <el-button round @click="$router.push('/demo')">
              <i class="el-icon-date"></i>
              {{ $t('faq.cta.bookDemo') }}
            </el-button> -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FaqPage',
  data() {
    return {
      searchQuery: '',
      activeCategory: 'all',
      expandedId: null,
      helpfulVotes: {},
      categories: [
        { id: 'all', icon: 'el-icon-menu' },
        { id: 'general', icon: 'el-icon-info' },
        { id: 'pricing', icon: 'el-icon-price-tag' },
        { id: 'features', icon: 'el-icon-s-tools' },
        { id: 'security', icon: 'el-icon-lock' },
        { id: 'account', icon: 'el-icon-user' },
        { id: 'technical', icon: 'el-icon-monitor' }
      ],
      faqItems: [
        { id: 'whatIsLpt', category: 'general' },
        { id: 'auditStandards', category: 'general' },
        { id: 'getStarted', category: 'general' },
        { id: 'pricingPlans', category: 'pricing' },
        { id: 'cancelSubscription', category: 'pricing' },
        { id: 'paymentMethods', category: 'pricing' },
        { id: 'customTemplates', category: 'features' },
        { id: 'supplierPortalWork', category: 'features' },
        { id: 'shareAuditsInternal', category: 'features' },
        { id: 'accessCost', category: 'features' },
        { id: 'shareAuditsExternal', category: 'features' },
        { id: 'offlineMode', category: 'technical' },
        { id: 'exportAudits', category: 'features' },
        { id: 'removeAccess', category: 'features' },
        { id: 'dataSecurity', category: 'security' },
        { id: 'backupHandling', category: 'security' },
        { id: 'auditorLimit', category: 'account' },
        { id: 'supportedLanguages', category: 'technical' },
        { id: 'auditManagementSupport', category: 'general' },
        { id: 'inexexperiencedUser', category: 'general' },
        { id: 'uploadFiles', category: 'features' },
        { id: 'multipleAuditors', category: 'account' },
        { id: 'buyLicences', category: 'account' },
        { id: 'addTemplates', category: 'features' },
        { id: 'templatePurchase', category: 'features' }
      ]
    }
  },
  computed: {
    quickTags() {
      return [
        this.$t('faq.quickTags.pricing'),
        this.$t('faq.quickTags.security'),
        this.$t('faq.quickTags.iso'),
        this.$t('faq.quickTags.trial')
      ]
    },
    filteredFaqs() {
      let items = this.faqItems
      if (this.activeCategory !== 'all') {
        items = items.filter(i => i.category === this.activeCategory)
      }
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase()
        items = items.filter(i => {
          const q = this.$t(`faq.items.${i.id}.q`).toLowerCase()
          const a = this.$t(`faq.items.${i.id}.a`).toLowerCase()
          return q.includes(query) || a.includes(query)
        })
      }
      return items
    }
  },
  methods: {
    handleSearch() {
      this.activeCategory = 'all'
    },
    clearSearch() {
      this.searchQuery = ''
      this.activeCategory = 'all'
    },
    toggleFaq(id) {
      this.expandedId = this.expandedId === id ? null : id
    },
    getCategoryCount(catId) {
      if (catId === 'all') return this.faqItems.length
      return this.faqItems.filter(i => i.category === catId).length
    },
    getCategoryTagType(cat) {
      const map = {
        general: '',
        pricing: 'warning',
        features: 'success',
        security: 'danger',
        account: 'info',
        technical: ''
      }
      return map[cat] || ''
    },
    voteHelpful(id, vote) {
      this.$set(this.helpfulVotes, id, vote)
      if (vote === 'yes') {
        this.$message.success(this.$t('faq.thanksFeedback'))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.faq-page {
  min-height: 100vh;
  background: #f8fafc;
}

// Hero
.faq-hero {
  position: relative;
  padding: 140px 20px 60px;
  background: linear-gradient(135deg, #1e1b4b 0%, #312e81 30%, #4338ca 60%, #6366f1 100%);
  overflow: hidden;
  text-align: center;

  .hero-bg-shapes {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;

    .shape {
      position: absolute;
      border-radius: 50%;
      opacity: 0.08;

      &.s1 {
        width: 600px;
        height: 600px;
        background: #fff;
        top: -200px;
        right: -100px;
      }
      &.s2 {
        width: 400px;
        height: 400px;
        background: #818cf8;
        bottom: -150px;
        left: -100px;
      }
      &.s3 {
        width: 250px;
        height: 250px;
        background: #c7d2fe;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }
  }

  .hero-content {
    position: relative;
    z-index: 2;
    max-width: 700px;
    margin: 0 auto;
  }

  .hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 18px;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 20px;
    color: #c7d2fe;
    font-size: 14px;
    margin-bottom: 24px;

    i {
      font-size: 16px;
    }
  }

  h1 {
    font-size: 42px;
    font-weight: 800;
    color: #fff;
    margin: 0 0 14px;
    letter-spacing: -0.5px;
    line-height: 1.2;
  }

  p {
    font-size: 17px;
    color: #c7d2fe;
    margin: 0 0 36px;
    line-height: 1.6;
  }
}

.search-box {
  position: relative;
  max-width: 560px;
  margin: 0 auto 20px;

  i.el-icon-search {
    position: absolute;
    left: 20px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 20px;
    color: #94a3b8;
  }

  input {
    width: 100%;
    padding: 18px 50px 18px 52px;
    font-size: 16px;
    border: 2px solid transparent;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 8px 32px rgba(0,0,0,0.15);
    outline: none;
    transition: all 0.3s;
    color: #1e293b;

    &:focus {
      border-color: #818cf8;
      box-shadow: 0 8px 32px rgba(99,102,241,0.25);
    }

    &::placeholder {
      color: #94a3b8;
    }
  }

  .search-clear {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    color: #94a3b8;
    font-size: 20px;
    transition: color 0.2s;

    &:hover {
      color: #475569;
    }
  }
}

.quick-links {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;

  .quick-tag {
    padding: 6px 16px;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.18);
    border-radius: 20px;
    color: #e0e7ff;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      background: rgba(255,255,255,0.22);
      transform: translateY(-1px);
    }
  }
}

// Container
.faq-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 20px 80px;
}

// Category Bar
.category-bar {
  display: flex;
  gap: 8px;
  padding: 24px 0 30px;
  overflow-x: auto;
  flex-wrap: wrap;

  &::-webkit-scrollbar {
    display: none;
  }
}

.category-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;

  i {
    font-size: 16px;
    color: #94a3b8;
  }

  .cat-count {
    font-size: 12px;
    background: #f1f5f9;
    color: #94a3b8;
    padding: 2px 8px;
    border-radius: 10px;
    font-weight: 600;
  }

  &:hover {
    border-color: #c7d2fe;
    color: #6366f1;
    background: #faf5ff;

    i {
      color: #6366f1;
    }
  }

  &.active {
    border-color: #6366f1;
    background: linear-gradient(135deg, #6366f1, #818cf8);
    color: white;
    box-shadow: 0 4px 12px rgba(99,102,241,0.25);

    i {
      color: white;
    }

    .cat-count {
      background: rgba(255,255,255,0.2);
      color: white;
    }
  }
}

// Search results
.search-results-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: #eff6ff;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #3b82f6;
  font-weight: 500;
}

// FAQ List
.faq-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.faq-card {
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  transition: all 0.3s;
  margin-bottom: 10px;

  &:hover {
    border-color: #c7d2fe;
    box-shadow: 0 4px 16px rgba(0,0,0,0.04);
  }

  &.expanded {
    border-color: #6366f1;
    box-shadow: 0 4px 20px rgba(99,102,241,0.1);
  }
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;

  &:hover {
    background: #fafbfd;
  }

  .q-left {
    display: flex;
    align-items: center;
    gap: 14px;
    flex: 1;
    min-width: 0;
  }

  .q-number {
    font-size: 13px;
    font-weight: 700;
    color: #c7d2fe;
    font-family: 'Courier New', monospace;
    flex-shrink: 0;
  }

  .q-category-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;

    &.general { background: #6366f1; }
    &.pricing { background: #f59e0b; }
    &.features { background: #10b981; }
    &.security { background: #ef4444; }
    &.account { background: #3b82f6; }
    &.technical { background: #8b5cf6; }
  }

  h3 {
    font-size: 15px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
    line-height: 1.5;
  }

  .q-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    margin-left: 16px;
  }

  .expand-icon {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    i {
      font-size: 14px;
      color: #64748b;
      transition: transform 0.3s;
    }

    &.rotated {
      background: #6366f1;
      i {
        color: white;
        transform: rotate(180deg);
      }
    }
  }
}

// Answer
.faq-answer {
  border-top: 1px solid #f1f5f9;

  .answer-content {
    padding: 20px 24px 16px;
    padding-left: 62px;

    p {
      font-size: 15px;
      line-height: 1.8;
      color: #475569;
      margin: 0;
    }
  }

  .answer-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 24px 16px 62px;

    .helpful-text {
      font-size: 13px;
      color: #94a3b8;
    }

    .helpful-buttons {
      display: flex;
      gap: 8px;
    }

    .helpful-btn {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px 14px;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      background: #fff;
      font-size: 12px;
      color: #64748b;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        border-color: #c7d2fe;
      }

      &.yes.voted {
        background: #ecfdf5;
        border-color: #10b981;
        color: #10b981;
      }

      &.no.voted {
        background: #fef2f2;
        border-color: #ef4444;
        color: #ef4444;
      }
    }
  }
}

.answer-slide-enter-active,
.answer-slide-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.answer-slide-enter,
.answer-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

// Empty state
.empty-state {
  text-align: center;
  padding: 60px 20px;

  i {
    font-size: 48px;
    color: #cbd5e1;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 18px;
    color: #475569;
    margin: 0 0 8px;
  }

  p {
    font-size: 14px;
    color: #94a3b8;
    margin: 0 0 20px;
  }
}

// FAQ item animation
.faq-item-enter-active,
.faq-item-leave-active {
  transition: all 0.3s ease;
}

.faq-item-enter,
.faq-item-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

// Contact CTA
.contact-cta {
  margin-top: 60px;
  padding: 60px 40px;
  background: linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%);
  border-radius: 24px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -80px;
    right: -80px;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: rgba(255,255,255,0.05);
  }

  .cta-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 20px;
    border-radius: 20px;
    background: rgba(255,255,255,0.12);
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 30px;
      color: #c7d2fe;
    }
  }

  h2 {
    font-size: 28px;
    font-weight: 700;
    color: #fff;
    margin: 0 0 10px;
  }

  p {
    font-size: 16px;
    color: #c7d2fe;
    margin: 0 0 28px;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }

  .cta-buttons {
    display: flex;
    justify-content: center;
    gap: 14px;
    flex-wrap: wrap;

    .el-button--primary {
      background: #fff;
      border-color: #fff;
      color: #4338ca;
      font-weight: 600;
      padding: 12px 28px;

      &:hover {
        background: #e0e7ff;
        border-color: #e0e7ff;
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(0,0,0,0.15);
      }
    }

    .el-button--default {
      background: transparent;
      border: 2px solid rgba(255,255,255,0.3);
      color: #fff;
      font-weight: 600;
      padding: 12px 28px;

      &:hover {
        border-color: #fff;
        background: rgba(255,255,255,0.1);
        transform: translateY(-2px);
      }
    }
  }
}

@media (max-width: 768px) {
  .faq-hero {
    padding: 100px 16px 40px;

    h1 {
      font-size: 28px;
    }
  }

  .faq-question {
    padding: 16px;

    h3 {
      font-size: 14px;
    }

    .q-right .el-tag {
      display: none;
    }
  }

  .faq-answer .answer-content {
    padding-left: 16px;
  }

  .faq-answer .answer-footer {
    padding-left: 16px;
    flex-direction: column;
    align-items: flex-start;
  }

  .contact-cta {
    padding: 40px 24px;
    border-radius: 16px;

    h2 {
      font-size: 22px;
    }
  }
}
</style>
